import { useRouteContext } from "@tanstack/react-router";
import { toError } from "fp-ts/Either";
import { flow, pipe } from "fp-ts/function";
import { getOrgAndSiteFromNames } from "../fetch-collection-site";
import { getOrganisationFromName } from "../fetch-organisation";
import { flowInto } from "../flow";
import * as OE from "../option-either";
import { createWrappedSWRHook } from "../swr";
import {
  createAuthedHookWithMap,
  createAuthedHookWithMappedStruct,
} from "./hook";
import { extractOrg, usePlace } from "./place";

const InvalidOrgSubdomain = {
  type: "NO_ORG_SUBDOMAIN_FOUND" as const,
};
type InvalidOrgSubdomain = typeof InvalidOrgSubdomain;

export const useOrganisation = pipe(
  createWrappedSWRHook(getOrganisationFromName, toError),
  flowInto(OE.chainW(extractOrg)),
  createAuthedHookWithMappedStruct,
  (useOrganisation) => () => {
    const { subdomain } = useRouteContext({ from: "__root__" });

    return useOrganisation(
      { organisationSubdomain: OE.of(subdomain) },
      ({ organisationSubdomain }) => [
        organisationSubdomain === undefined
          ? undefined
          : {
              organisationSubdomain,
            },
      ],
    );
  },
);

export const useValidOrganisation = flow(
  useOrganisation,
  OE.noneAndErrorToUndefined,
);

export const useOrgWithSite = pipe(
  createWrappedSWRHook(getOrgAndSiteFromNames, toError),
  flowInto(OE.chainW(extractOrg)),
  createAuthedHookWithMap,
  (useOrgAndSite) => () =>
    useOrgAndSite(usePlace(), (place) => [
      place === undefined
        ? undefined
        : {
            organisationSubdomain: place.orgName,
            collectionSiteSlug: place.siteSlug,
          },
    ]),
);
